//@ts-check
import { mustBeHtmlElement } from "common/dom-utils";
import { isPresent } from "common/utils/blank";

/**
 * Origin: Checkout::FontFamilyLicensedPricesSerializer#price_to_h
 * @typedef {object} PricePayload
 * @property {number | null} amount
 * @property {number | null} amount_without_discount
 * @property {string} formatted (0 if amount is null)
 * @property {string} formatted_amount_without_discount
 * @property {string} formatted_amount
 * @property {string} currency
 * @property {string} currency_suffix
 * @property {boolean | null} included
 * @property {string | null} code
 */

/**
 * Origin: Checkout::FontFamilyLicensedPricesSerializer#entry_payload
 * @typedef {object} EntryPayload
 * @property {{ id: number, type: string, tid: string }} purchasable
 * @property {PricePayload | null} price
 * @property {{ is_added: boolean, is_auto_included: boolean, included_reason: string | null }} cart_status
 */

/**
 * Represents a purchasable item row in the purchasing section of the font detail page.
 */
export class PurchasableComponent {
  constructor(purchasableElem) {
    this.element = mustBeHtmlElement(purchasableElem, "purchasableElem");
  }

  get purchasableId() {
    return this.element.dataset.purchasableId;
  }

  get purchasableType() {
    return this.element.dataset.purchasableType;
  }

  get purchasableData() {
    return {
      type: this.purchasableType,
      id: this.purchasableId,
    };
  }

  get isFamily() {
    return this.purchasableType === "FontFamily";
  }

  get isPackage() {
    return this.purchasableType === "FontPackage";
  }

  get isStyle() {
    return this.purchasableType === "FontStyle";
  }

  // Add / Remove / Included button
  get button() {
    if (!this._button) {
      const button = this.element.querySelector("a.buy-button");
      this._button = mustBeHtmlElement(button, "buy button");
    }
    return this._button;
  }

  set highlighted(v) {
    if (v) {
      this.element.classList.add("purchased");
    } else {
      this.element.classList.remove("purchased");
    }
  }

  makeRemovable(item) {
    this.highlighted = true;

    // Should be compative with remove_from_cart_link in app/helpers/store/application_helper.rb
    this.button.textContent = "Remove";
    this.button.classList.remove("buy-button--included", "buy-button--add");
    this.button.classList.add("buy-button--remove");
    this.button.dataset.controller = "cart-add-remove-button";
    this.button.dataset.action = "click->cart-add-remove-button#remove";
    this.button.dataset.method = "delete";

    const queryString = new URLSearchParams(this.purchasableData).toString();
    this.button.setAttribute(
      "href",
      `/shopping_cart_items.json?${queryString}`
    );
  }

  makeIncluded() {
    this.highlighted = true;

    // Should be compative with included_in_cart_link in app/helpers/store/application_helper.rb
    this.button.textContent = "Included";
    this.button.classList.remove("buy-button--remove", "buy-button--add");
    this.button.classList.add("buy-button--included");
    this.button.dataset.controller = "cart-add-remove-button";
    this.button.dataset.action = "click->cart-add-remove-button#noop";
    this.button.removeAttribute("data-method");
    this.button.setAttribute("href", "#");
  }

  makeAddable() {
    this.highlighted = false;

    // Should be compative with add_to_cart_link in app/helpers/store/application_helper.rb
    this.button.textContent = "Add";
    this.button.classList.remove("buy-button--remove", "buy-button--included");
    this.button.classList.add("buy-button--add");
    this.button.dataset.controller = "cart-add-remove-button";
    this.button.dataset.method = "post";
    this.button.dataset.action = "click->cart-add-remove-button#add";
    const queryString = new URLSearchParams(this.purchasableData).toString();
    this.button.setAttribute(
      "href",
      `/shopping_cart_items.json?${queryString}`
    );
  }

  /**
   * @param {EntryPayload} entryPayload
   */
  setPrice(entryPayload) {
    const pricePayload = entryPayload.price;

    // Don't show the price if the item is included automatically,
    // either because the family/package price has been reached, or the family/package itself has been added.
    // Here isIncluded is true even if the item is explicitly added to the cart and the family/package price has been reached.
    const isPriceShowable = pricePayload !== null && !pricePayload.included;

    const priceElement = mustBeHtmlElement(
      this.element.querySelector(".font-style-price"),
      "price element"
    );

    if (!isPriceShowable) {
      priceElement.innerHTML = `&nbsp;`;
      return;
    }

    let crossedOutHtml = "";

    // For debugging
    // priceElement.title = JSON.stringify(entryPayload, null, 2);

    const isCrossedOutPriceShowable =
      isPresent(pricePayload?.amount_without_discount) &&
      pricePayload.amount_without_discount > pricePayload.amount;

    if (isCrossedOutPriceShowable) {
      crossedOutHtml = `<s>${pricePayload.formatted_amount_without_discount}</s>`;
    }

    if (isPriceShowable && isCrossedOutPriceShowable) {
      // Price & discounted price
      // <s>90</s> 35 CHF
      priceElement.innerHTML = `<s>${pricePayload.formatted_amount_without_discount}</s> ${pricePayload.formatted_amount}${pricePayload.currency_suffix}`;
    } else if (isPriceShowable && !isCrossedOutPriceShowable) {
      // Only Price
      // 90 CHF
      priceElement.innerHTML = `${pricePayload.formatted_amount}${pricePayload.currency_suffix}`;
    } else if (pricePayload?.included && isCrossedOutPriceShowable) {
      // Original price only (included)
      // <s>90 CHF</s>
      priceElement.innerHTML = `<s>${pricePayload.formatted_amount_without_discount}${pricePayload.currency_suffix}</s>`;
    } else {
      priceElement.innerHTML = `&nbsp;`;
    }
  }
}
